import { IConnectionSummary } from 'dto/device-dto';
import { DeploymentStatus, UnifiedDeploymentUnitType } from './deployment-dto';
import { DEPLOYMENT_TARGET_STATUS } from './target-dto';
import { transform } from 'dto/transformer';
import { DeploymentGroupType } from './deployment-group-dto';
import { isNullOrEmpty } from 'common-utils';

export const DeploymentType = DeploymentGroupType;
export interface GetDeploymentDetailsQueryRequest {
    includeDeviceConnectivity?: boolean;
}

export class DeploymentDetailsUnitDto {
    constructor(private readonly _object: any = {}) {}

    get id(): number {
        return this._object.id;
    }

    get uuid(): string {
        return this._object.uuid || '';
    }

    get name(): string {
        return this._object.name || '';
    }

    get version(): string {
        return this._object.version || '';
    }

    get displayName(): string {
        if (this.type === UnifiedDeploymentUnitType.COMPONENT_VERSION) {
            return `${this.name} (${this.version})`;
        }
        return this.name;
    }

    get type(): UnifiedDeploymentUnitType {
        return this._object.type;
    }
}

export class DeploymentDetailsDeviceDto {
    constructor(
        private readonly _object: any = {},
    ) {}

    get deviceName(): string {
        return this._object.deviceName;
    }

    get serialNumber(): string {
        return this._object.serialNumber || '';
    }

    get esn(): string {
        return this._object.esn || '';
    }

    get assignedToGroupAt(): string {
        return this._object.assignedToGroupAt;
    }

    get lastJobSuccessAt(): Date {
        return this._object.lastJobSuccessAt ? new Date(this._object.lastJobSuccessAt) : null;
    }

    get trampolineVersion(): string {
        return this._object.trampolineVersion || '';
    }

    get firmwareVersion(): string {
        return this._object.firmwareVersion || '';
    }

    get executionId(): string {
        return this._object.executionId || '';
    }

    get executionNumber(): number {
        return this._object.executionNumber;
    }

    get executionStatus(): string {
        return this._object.executionStatus || '';
    }

    get isRerunExecution(): boolean {
        return !!this._object.isRerunExecution;
    }

    get executionCreatedAt(): Date {
        return this._object.executionCreatedAt ? new Date(this._object.executionCreatedAt) : null;
    }

    get connectivity(): IConnectionSummary {
        return this._object.connectivity;
    }
}

export class DeploymentDetailsDeviceGroupDto {
    constructor(private readonly _object: any = {}) {}

    get id(): number {
        return this._object.id;
    }

    get name(): string {
        return this._object.deviceGroupName || '';
    }

    get label(): string {
        return this._object.deviceGroupLabel || this.name;
    }

    get description(): string {
        return this._object.deviceGroupDescription || '';
    }

    get type(): string {
        return this._object.deviceGroupType || '';
    }

    get timezone(): string {
        return this._object.deviceGroupTimezone || '';
    }

    get devices(): DeploymentDetailsDeviceDto[] {
        return Object.entries(this._object.devices || {}).map(
            ([deviceUid, device]) =>
                new DeploymentDetailsDeviceDto(
                    {
                        ...(device as object),
                        deviceName: deviceUid,
                    },
                ),
        );
    }
}

export class DeploymentDetailsTargetDto {
    constructor(private readonly _object: any = {}) {}

    get status(): DEPLOYMENT_TARGET_STATUS {
        return this._object.status;
    }

    get scheduleDateTime(): Date {
        return this._object.scheduleDateTime ? new Date(this._object.scheduleDateTime) : null;
    }

    get installDateTime(): Date {
        return this._object.installDateTime ? new Date(this._object.installDateTime) : null;
    }

    get downloadOnly(): boolean {
        return !!this._object.downloadOnly;
    }

    get dpuOverride(): boolean {
        return !!this._object.dpuOverride;
    }

    get executionId(): string {
        return this._object.executionId || '';
    }

    get deviceGroups(): DeploymentDetailsDeviceGroupDto[] {
        return transform(DeploymentDetailsDeviceGroupDto, this._object.deviceGroups as unknown[] || []);
    }
}

export class DeploymentDetails {
    constructor(private readonly _object: any = {}) {}

    get id(): number {
        return this._object.id;
    }

    get name(): string {
        return this._object.label || '';
    }

    get status(): DeploymentStatus {
        return this._object.status;
    }

    get deploymentGroupId(): number {
        return this._object.deploymentGroupId;
    }

    get sequenceNumber(): number {
        return this._object.sequenceNumber;
    }

    get autoComplete(): boolean {
        return !!this._object.autoComplete;
    }

    get createdAt(): Date {
        return this._object.createdAt ? new Date(this._object.createdAt) : null;
    }

    get units(): DeploymentDetailsUnitDto[] {
        return transform(DeploymentDetailsUnitDto, (this._object.units as unknown[]) ?? []);
    }

    get targets(): DeploymentDetailsTargetDto[] {
        return transform(DeploymentDetailsTargetDto, (this._object.targets as unknown[]) ?? []);
    }

    get deviceGroups(): DeploymentDetailsDeviceGroupDto[] {
        return this.targets.flatMap((target) => target.deviceGroups);
    }

    get devices(): DeploymentDetailsDeviceDto[] {
        return this.deviceGroups.flatMap((group) => group.devices);
    }

    get type() {
        if (isNullOrEmpty(this.units)) {
            return DeploymentType.ASSURE;
        }
        // All units in the same deployment must be the same type
        return this.units[0]?.type === UnifiedDeploymentUnitType.COMPONENT_VERSION ? DeploymentType.RAPTOR : DeploymentType.ASSURE;
    }
}
